import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "stichworte";

export type Stichwort = {
  readonly id: string;
  readonly stichwort: string;
  readonly kategorie?: string;
  readonly einsatzbericht?: string;
};

function useStichworteSyncModule(): SyncModule<ReadonlyArray<Stichwort>> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Keywords are a new-Datenportal-only resource — the legacy portal has
      // no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchStichworteNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchStichworteNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Stichwort>> {
  const response = await fetch(`${baseUrl}/api/device/sync/keywords`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.keywords ?? []).map(parseStichwortNew);
}

function parseStichwortNew(entry: any): Stichwort {
  return {
    id: entry.id,
    stichwort: entry.keyword,
    kategorie: entry.category || undefined,
    einsatzbericht: entry.incidentReport || undefined,
  };
}

export function useStichworteSync() {
  const module = useStichworteSyncModule();
  useSyncModuleRegistration(module);
}

export function useStichworte(): ReadonlyArray<Stichwort> {
  return useSyncData(syncModuleId) ?? [];
}
